<template>
  <div class="panel-resizer" :style="{ width: width + 'px' }">
    <slot></slot>
    <div
      class="panel-resizer__resize"
      @mousedown="onMouseDown"
      @touchstart="onTouchStart"
    >
      <font-awesome-icon icon="fa-solid fa-grip-lines-vertical" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, ref } from "vue";

interface Props {
  direction?: string;
  minSize?: number;
  maxSize?: number;
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  direction: "horizontal",
  size: window.innerWidth / 2, // Default to half of the window width
  minSize: 250, // Minimum size in pixels
  maxSize: window.innerWidth * 0.8, // Maximum size in pixels (80% of window width)
});

const width = ref(props.size); // Set initial width to 2/3 of the window width
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

const onMouseDown = (e: MouseEvent) => {
  isResizing.value = true;
  startX.value = e.clientX;
  startWidth.value = width.value || 0;
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
};

const onTouchStart = (e: TouchEvent) => {
  isResizing.value = true;
  startX.value = e.touches[0].clientX;
  startWidth.value = width.value;
  document.addEventListener("touchmove", onTouchMove, { passive: false });
  document.addEventListener("touchend", onTouchEnd);
  e.preventDefault();
};

const onMouseMove = (e: MouseEvent) => {
  if (!isResizing.value) return;
  const newWidth = startWidth.value + (e.clientX - startX.value);
  width.value = Math.max(Math.min(newWidth, props.maxSize), props.minSize);
};

const onTouchMove = (e: TouchEvent) => {
  if (!isResizing.value) return;
  const newWidth = startWidth.value + (e.touches[0].clientX - startX.value);
  width.value = Math.max(Math.min(newWidth, props.maxSize), props.minSize);
  e.preventDefault();
};

const onMouseUp = () => {
  isResizing.value = false;
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
};

const onTouchEnd = () => {
  isResizing.value = false;
  document.removeEventListener("touchmove", onTouchMove);
  document.removeEventListener("touchend", onTouchEnd);
};

const handleResize = () => {
  // Adjust sidebar width if it's bigger than available space
  if (width.value > props.maxSize) {
    width.value = Math.max(props.minSize, window.innerWidth * 0.6);
  }
};

// Add window resize event listener
onBeforeMount(() => {
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
  document.removeEventListener("touchmove", onTouchMove);
  document.removeEventListener("touchend", onTouchEnd);
  window.removeEventListener("resize", handleResize);
});
</script>

<style lang="scss">
.panel-resizer {
  flex: 0 0 auto;
  overflow: hidden;
  border-right: 1px solid #e0e0e0;
  display: flex;

  &__resize {
    width: 12px;
    cursor: ew-resize;
    background-color: #e0e0e0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
